import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import {
  ArrowLeft, Truck, Check, Plus, Trash2, Save, RefreshCw, Package,
} from 'lucide-react';
import clsx from 'clsx';

const API_BASE = process.env.REACT_APP_API_URL || '/api/v1';

const fmtDatum = (s: any) => {
  if (!s) return '—';
  const d = new Date(String(s));
  if (isNaN(d.getTime())) return String(s);
  const p = (n: number) => String(n).padStart(2, '0');
  return p(d.getDate()) + '.' + p(d.getMonth() + 1) + '.' + d.getFullYear();
};

// barva polja po zaupanju vrstice (ocr_confidence 0..1)
const poljeCls = (conf: any, vrednost: any) => {
  const prazno = vrednost === null || vrednost === undefined || vrednost === '';
  const neberljivo = String(vrednost).toLowerCase() === 'neberljivo';
  if (neberljivo) return 'border-red-400 bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300';
  if (prazno) return 'border-gray-300 dark:border-gray-600';
  if (conf === null || conf === undefined) return 'border-gray-300 dark:border-gray-600';
  if (conf >= 0.85) return 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30';
  if (conf >= 0.60) return 'border-amber-300 bg-amber-50 dark:bg-amber-950/30';
  return 'border-red-400 bg-red-50 dark:bg-red-950/40';
};

interface Vracilo {
  id: string;
  filiala: string | null;
  vozicki_rlc: string | null;
  palete: string | null;
  box: string | null;
  podpis: string | null;
  ocr_confidence: number | null;
  _dirty?: boolean;
}

export function LogistikaTransportPage() {
  const { transportId } = useParams<{ transportId: string }>();
  const navigate = useNavigate();
  const selectedCompany = useAuthStore((s: any) => s.selectedCompany);
  const baza = selectedCompany;

  const [transport, setTransport] = useState<any>(null);
  const [vracila, setVracila] = useState<Vracilo[]>([]);
  const [nalagam, setNalagam] = useState(true);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [shranjujem, setShranjujem] = useState(false);

  const naloziPodatke = useCallback(() => {
    if (!baza || !transportId) return;
    setNalagam(true);
    api.get('/logistika/' + baza.id + '/transport/' + transportId)
      .then((r: any) => {
        setTransport(r.data);
        setVracila((r.data.vracila || []).map((v: any) => ({ ...v, _dirty: false })));
      })
      .catch(() => toast.error('Napaka pri nalaganju transporta'))
      .finally(() => setNalagam(false));
  }, [baza, transportId]);

  useEffect(naloziPodatke, [naloziPodatke]);

  // sken (cel PDF) prek kratkozivnega tokena
  useEffect(() => {
    if (!baza || !transportId) return;
    api.get('/logistika/' + baza.id + '/transport/' + transportId + '/obrazec-token')
      .then((r: any) => {
        setPdfUrl(API_BASE + '/logistika/' + baza.id + '/transport/' +
          transportId + '/obrazec-public?token=' + r.data.token);
      })
      .catch(() => setPdfUrl(null));
  }, [baza, transportId]);

  const posodobiPolje = (id: string, polje: keyof Vracilo, vrednost: string) => {
    setVracila((vs) => vs.map((v) =>
      v.id === id ? { ...v, [polje]: vrednost, _dirty: true } : v));
  };

  const shraniVrstico = (v: Vracilo) => {
    if (!baza) return;
    setShranjujem(true);
    api.put('/logistika/' + baza.id + '/vracilo/' + v.id, {
      filiala: v.filiala, vozicki_rlc: v.vozicki_rlc,
      palete: v.palete, box: v.box, podpis: v.podpis,
    })
      .then(() => {
        setVracila((vs) => vs.map((x) => x.id === v.id ? { ...x, _dirty: false } : x));
        toast.success('Shranjeno');
      })
      .catch(() => toast.error('Napaka pri shranjevanju'))
      .finally(() => setShranjujem(false));
  };

  const shraniVse = () => {
    const spremenjeni = vracila.filter((v) => v._dirty);
    if (spremenjeni.length === 0) { toast('Ni sprememb'); return; }
    setShranjujem(true);
    Promise.all(spremenjeni.map((v) =>
      api.put('/logistika/' + baza.id + '/vracilo/' + v.id, {
        filiala: v.filiala, vozicki_rlc: v.vozicki_rlc,
        palete: v.palete, box: v.box, podpis: v.podpis,
      })))
      .then(() => {
        setVracila((vs) => vs.map((x) => ({ ...x, _dirty: false })));
        toast.success(spremenjeni.length + ' vrstic shranjenih');
      })
      .catch(() => toast.error('Napaka pri shranjevanju'))
      .finally(() => setShranjujem(false));
  };

  const dodajVrstico = () => {
    if (!baza || !transportId) return;
    api.post('/logistika/' + baza.id + '/vracilo', {
      transport_id: transportId, filiala: '', vozicki_rlc: '', palete: '', box: '',
    })
      .then((r: any) => {
        setVracila((vs) => [...vs, {
          id: r.data.id, filiala: '', vozicki_rlc: '', palete: '',
          box: '', podpis: null, ocr_confidence: null, _dirty: false,
        }]);
        toast.success('Dodana vrstica');
      })
      .catch(() => toast.error('Napaka pri dodajanju'));
  };

  const izbrisiVrstico = (id: string) => {
    if (!baza) return;
    api.delete('/logistika/' + baza.id + '/vracilo/' + id)
      .then(() => {
        setVracila((vs) => vs.filter((v) => v.id !== id));
        toast.success('Izbrisano');
      })
      .catch(() => toast.error('Napaka pri brisanju'));
  };

  const potrdi = () => {
    if (!baza || !transportId) return;
    const dirty = vracila.some((v) => v._dirty);
    if (dirty) { toast.error('Najprej shranite spremembe'); return; }
    api.post('/logistika/' + baza.id + '/transport/' + transportId + '/potrdi')
      .then(() => {
        toast.success('Transport potrjen');
        naloziPodatke();
      })
      .catch(() => toast.error('Napaka pri potrjevanju'));
  };

  const imaSprememb = vracila.some((v) => v._dirty);

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button onClick={() => navigate('/logistika')}
          className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <Truck className="w-6 h-6 text-blue-500" />
          Prevoznica {transport?.st_prevoznice || '…'}
        </h1>
        {transport?.status && (
          <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium',
            transport.status === 'potrjeno'
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300'
              : 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300')}>
            {transport.status}
          </span>
        )}
        <button onClick={naloziPodatke} title="Osveži"
          className="ml-auto p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700">
          <RefreshCw className={clsx('w-4 h-4', nalagam && 'animate-spin')} />
        </button>
      </div>

      {transport && (
        <div className="mb-4 space-y-1.5">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
            <div><span className="text-gray-400">Datum:</span> {fmtDatum(transport.datum)}</div>
            <div><span className="text-gray-400">Prevoznik:</span> {transport.prevoznik || '—'}</div>
            <div>
              <span className="text-gray-400">Voznik:</span> {transport.voznik || '—'}
              {transport.voznik_tel && <span className="text-gray-400"> ({transport.voznik_tel})</span>}
            </div>
            <div><span className="text-gray-400">Reg. št.:</span> {transport.reg_st || '—'}</div>
            {transport.km != null && <div><span className="text-gray-400">Km:</span> {transport.km}</div>}
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
            {transport.dostav != null && (
              <div><span className="text-gray-400">Dostav:</span> <span className="font-medium">{transport.dostav}</span></div>
            )}
            {transport.prevozna_smer && (
              <div><span className="text-gray-400">Smer:</span> {transport.prevozna_smer}</div>
            )}
            {transport.nakladisce && (
              <div><span className="text-gray-400">Nakladišče:</span> {transport.nakladisce}</div>
            )}
            {transport.stevilo_palet != null && (
              <div><span className="text-gray-400">Palet:</span> {transport.stevilo_palet}</div>
            )}
            {transport.embalaza_povzetek && (
              <div><span className="text-gray-400">Embalaža:</span> {transport.embalaza_povzetek}</div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* LEVO: sken */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden h-[75vh]">
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-600 dark:text-gray-300">
            Sken dokumenta
          </div>
          {pdfUrl ? (
            <iframe src={pdfUrl} className="w-full h-[calc(75vh-2.5rem)]" title="Sken" />
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-400">
              Sken ni na voljo
            </div>
          )}
        </div>

        {/* DESNO: vracila (urejljiva) */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow overflow-auto h-[75vh] flex flex-col">
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800 z-10">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1.5">
              <Package className="w-4 h-4" /> Vrnjena embalaža po filialah
              <span className="text-gray-400 font-normal">
                ({vracila.length}{transport?.dostav != null ? ' / ' + transport.dostav : ''})
              </span>
              {transport?.dostav != null && vracila.length !== transport.dostav && (
                <span className="text-amber-500 text-xs font-normal">
                  — ne ustreza dostav!
                </span>
              )}
            </span>
            <button onClick={dodajVrstico}
              className="text-xs px-2 py-1 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> Dodaj
            </button>
          </div>

          <div className="flex-1 p-3 space-y-2">
            {vracila.length === 0 && (
              <div className="text-center text-gray-400 py-8 text-sm">
                {nalagam ? 'Nalagam …' : 'Ni vračil. Dodajte ročno, če jih je OCR spustil.'}
              </div>
            )}
            {vracila.map((v) => (
              <div key={v.id}
                className={clsx('rounded-lg border p-3',
                  v._dirty ? 'border-blue-400 bg-blue-50/40 dark:bg-blue-950/20'
                    : 'border-gray-200 dark:border-gray-700')}>
                <div className="flex items-center gap-2 mb-2">
                  <input value={v.filiala || ''}
                    onChange={(e) => posodobiPolje(v.id, 'filiala', e.target.value)}
                    placeholder="Filiala"
                    className="flex-1 px-2 py-1 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white text-sm font-medium" />
                  {v._dirty && (
                    <button onClick={() => shraniVrstico(v)} disabled={shranjujem}
                      title="Shrani vrstico"
                      className="p-1.5 rounded bg-blue-600 text-white hover:bg-blue-700">
                      <Save className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button onClick={() => izbrisiVrstico(v.id)} title="Izbriši"
                    className="p-1.5 rounded text-red-500 hover:bg-red-50 dark:hover:bg-red-950/40">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Vozički RLC</label>
                    <input value={v.vozicki_rlc || ''}
                      onChange={(e) => posodobiPolje(v.id, 'vozicki_rlc', e.target.value)}
                      className={clsx('w-full px-2 py-1 rounded border text-sm dark:bg-gray-700 dark:text-white',
                        poljeCls(v.ocr_confidence, v.vozicki_rlc))} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Palete</label>
                    <input value={v.palete || ''}
                      onChange={(e) => posodobiPolje(v.id, 'palete', e.target.value)}
                      className={clsx('w-full px-2 py-1 rounded border text-sm dark:bg-gray-700 dark:text-white',
                        poljeCls(v.ocr_confidence, v.palete))} />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-0.5">Box</label>
                    <input value={v.box || ''}
                      onChange={(e) => posodobiPolje(v.id, 'box', e.target.value)}
                      className={clsx('w-full px-2 py-1 rounded border text-sm dark:bg-gray-700 dark:text-white',
                        poljeCls(v.ocr_confidence, v.box))} />
                  </div>
                </div>
                {v.ocr_confidence != null && (
                  <div className="mt-1.5 text-xs text-gray-400">
                    Zaupanje OCR: {Math.round(v.ocr_confidence * 100)}%
                    {v.ocr_confidence < 0.85 && <span className="text-amber-500 ml-1">— preverite</span>}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="px-3 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center gap-2 sticky bottom-0 bg-white dark:bg-gray-800">
            <button onClick={shraniVse} disabled={!imaSprememb || shranjujem}
              className={clsx('px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5',
                imaSprememb
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed')}>
              <Save className="w-4 h-4" /> Shrani vse
            </button>
            <button onClick={potrdi} disabled={imaSprememb}
              className={clsx('px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 ml-auto',
                imaSprememb
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                  : 'bg-emerald-600 text-white hover:bg-emerald-700')}>
              <Check className="w-4 h-4" /> Potrdi transport
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
